const wrapperSelector = '[data-size-wrapper]';
const observedOptions = new WeakSet();
const pendingWrappers = new Set();
let animationFrame;

function schedule(wrapper) {
  if (!(wrapper instanceof HTMLElement)) return;

  pendingWrappers.add(wrapper);
  if (animationFrame) return;

  animationFrame = requestAnimationFrame(() => {
    pendingWrappers.forEach(layout);
    pendingWrappers.clear();
    animationFrame = undefined;
  });
}

function register(wrapper) {
  if (!(wrapper instanceof HTMLElement)) return;

  const options = wrapper.querySelector('[data-size-options]');
  if (options instanceof HTMLElement && !observedOptions.has(options)) {
    observedOptions.add(options);
    resizeObserver?.observe(options);
  }

  schedule(wrapper);
}

function registerWithin(root) {
  if (!(root instanceof Element)) return;

  if (root.matches(wrapperSelector)) register(root);
  root.querySelectorAll(wrapperSelector).forEach(register);
}

function layout(wrapper) {
  if (!wrapper.isConnected) return;

  const options = wrapper.querySelector('[data-size-options]');
  const more = wrapper.querySelector('[data-size-more]');
  if (!(options instanceof HTMLElement) || !(more instanceof HTMLElement)) return;

  const items = Array.from(options.querySelectorAll('[data-size-item]'));
  if (items.length === 0) return;

  // Restore every chip before measuring, so a resize can reveal items again.
  items.forEach((item) => (item.style.display = ''));
  more.hidden = true;

  const gap = Number.parseFloat(getComputedStyle(options).columnGap) || 6;
  const availableWidth = options.clientWidth;
  const itemWidths = items.map((item) => item.getBoundingClientRect().width);
  const allItemsWidth = itemWidths.reduce((width, itemWidth, index) => width + itemWidth + (index ? gap : 0), 0);

  if (allItemsWidth <= availableWidth) return;

  const moreText = options.dataset.moreText || 'more';
  let visibleCount = 0;
  let usedWidth = 0;

  for (const [index, itemWidth] of itemWidths.entries()) {
    const remaining = items.length - index - 1;
    const itemGap = visibleCount ? gap : 0;
    let requiredWidth = usedWidth + itemGap + itemWidth;

    if (remaining > 0) {
      more.textContent = `+${remaining} ${moreText}`;
      more.hidden = false;
      requiredWidth += gap + more.getBoundingClientRect().width;
    }

    if (requiredWidth > availableWidth && visibleCount > 0) break;

    usedWidth += itemGap + itemWidth;
    visibleCount += 1;
  }

  const remaining = items.length - visibleCount;
  items.forEach((item, index) => (item.style.display = index < visibleCount ? '' : 'none'));

  if (remaining > 0) {
    more.textContent = `+${remaining} ${moreText}`;
    more.hidden = false;
  } else {
    more.hidden = true;
  }
}

const resizeObserver =
  typeof ResizeObserver === 'undefined'
    ? null
    : new ResizeObserver((entries) => {
        entries.forEach((entry) => register(entry.target.closest(wrapperSelector)));
      });

function initialize() {
  document.querySelectorAll(wrapperSelector).forEach(register);

  const mutations = new MutationObserver((records) => {
    records.forEach((record) => record.addedNodes.forEach(registerWithin));
  });
  mutations.observe(document.body, { childList: true, subtree: true });

  window.addEventListener('resize', () => document.querySelectorAll(wrapperSelector).forEach(schedule), { passive: true });
  document.addEventListener('shopify:section:load', () => document.querySelectorAll(wrapperSelector).forEach(register));
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initialize, { once: true });
} else {
  initialize();
}
